import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { loadStripe, Stripe, StripeEmbeddedCheckout } from '@stripe/stripe-js';
import { formatOrderIdForDisplay } from '../shared/orderUtils';
import './PaymentView.css';

type WidgetTone = 'info' | 'success' | 'warning' | 'error';

interface WidgetStatus {
    message: string;
    tone: WidgetTone;
}

interface StripePaymentProps {
    clientSecret: string | null | undefined;
    publishableKey: string | null | undefined;
    orderId?: string | null;
    amount?: number | null;
    currency?: string | null;
    isActive: boolean;
    onPaymentComplete?: () => void;
}

const stripeInstanceCache = new Map<string, Promise<Stripe | null>>();

async function getStripeInstance(publishableKey: string): Promise<Stripe | null> {
    if (!stripeInstanceCache.has(publishableKey)) {
        stripeInstanceCache.set(
            publishableKey,
            loadStripe(publishableKey)
        );
    }

    return stripeInstanceCache.get(publishableKey) ?? null;
}

const StripePayment: React.FC<StripePaymentProps> = ({
    clientSecret,
    publishableKey,
    orderId,
    amount,
    currency,
    isActive,
    onPaymentComplete
}) => {
    const containerId = useMemo(
        () => `stripe-checkout-${Math.random().toString(36).slice(2, 10)}`,
        []
    );
    const checkoutRef = useRef<StripeEmbeddedCheckout | null>(null);
    const [status, setStatus] = useState<WidgetStatus | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const lastPublishableKeyRef = useRef<string | null>(null);
    const resolvedCurrency = typeof currency === 'string' && currency.length > 0 ? currency : 'GBP';

    const teardownCheckout = useCallback(async () => {
        const checkout = checkoutRef.current;

        if (!checkout) {
            return;
        }

        checkoutRef.current = null;

        try {
            if (typeof checkout.unmount === 'function') {
                checkout.unmount();
            }
        } catch (error) {
            console.warn('Failed to unmount Stripe checkout cleanly', error);
        }

        try {
            const destroy = (checkout as { destroy?: () => Promise<void> | void }).destroy;
            if (typeof destroy === 'function') {
                await destroy.call(checkout);
            }
        } catch (error) {
            console.warn('Failed to fully destroy Stripe checkout', error);
        }

        const containerElement = document.getElementById(containerId);
        if (containerElement) {
            containerElement.innerHTML = '';
        }
    }, [containerId]);

    useEffect(() => {
        let cancelled = false;

        async function mountStripeCheckout() {
            if (!isActive) {
                await teardownCheckout();
                setIsLoading(false);
                setStatus(null);
                return;
            }

            if (!clientSecret || !publishableKey) {
                setStatus({
                    message: 'Missing Stripe configuration. Please contact support to complete payment.',
                    tone: 'error'
                });
                return;
            }

            setIsLoading(true);
            setStatus({
                message: 'Preparing your secure payment form…',
                tone: 'info'
            });

            try {
                if (lastPublishableKeyRef.current !== publishableKey) {
                    lastPublishableKeyRef.current = publishableKey;
                }

                const stripe = await getStripeInstance(publishableKey);

                if (cancelled) {
                    return;
                }

                await teardownCheckout();

                if (!stripe) {
                    throw new Error('Stripe.js failed to load. Please refresh and try again.');
                }

                const checkout = await stripe.initEmbeddedCheckout({
                    clientSecret,
                    onComplete: async () => {
                        if (cancelled) {
                            return;
                        }
                        void teardownCheckout();
                        setIsLoading(true);
                        setStatus({
                            message: "Payment complete! Confirming your order...",
                            tone: 'info'
                        });

                        // Extract session ID from client secret
                        const sessionId = clientSecret.split('_secret_')[0];

                        // Call backend to confirm payment and update order
                        try {
                            if (!orderId) {
                                throw new Error('Order ID is missing');
                            }

                            const confirmResponse = await fetch(`/api/stripe/orders/${orderId}/confirm`, {
                                method: 'POST',
                                headers: {
                                    'Content-Type': 'application/json'
                                },
                                body: JSON.stringify({ sessionId })
                            });

                            if (!confirmResponse.ok) {
                                const errorData = await confirmResponse.json() as { error?: string };
                                throw new Error(errorData.error || 'Failed to confirm payment');
                            }

                            setIsLoading(false);
                            setStatus({
                                message: "Payment confirmed! Your order has been placed.",
                                tone: 'success'
                            });
                            onPaymentComplete?.();
                        } catch (error) {
                            console.error('Failed to confirm Stripe payment:', error);
                            setIsLoading(false);
                            setStatus({
                                message: error instanceof Error ? error.message : 'Failed to confirm payment. Please contact support.',
                                tone: 'error'
                            });
                        }
                    }
                });

                if (cancelled) {
                    try {
                        checkout.unmount();
                        const destroy = (checkout as { destroy?: () => Promise<void> | void }).destroy;
                        if (typeof destroy === 'function') {
                            await destroy.call(checkout);
                        }
                    } catch (teardownError) {
                        console.warn('Failed to cancel Stripe checkout cleanly', teardownError);
                    }
                    return;
                }

                checkoutRef.current = checkout;

                checkout.mount(`#${containerId}`);

                if (!cancelled) {
                    setIsLoading(false);
                    setStatus(null);
                }
            } catch (error) {
                if (cancelled) {
                    return;
                }

                console.error('Stripe embedded checkout error', error);
                setIsLoading(false);
                const message = error instanceof Error ? error.message : 'Failed to load the payment widget.';
                setStatus({
                    message,
                    tone: 'error'
                });
            }
        }

        mountStripeCheckout();

        return () => {
            cancelled = true;
            void teardownCheckout();
        };
    }, [clientSecret, publishableKey, isActive, containerId, onPaymentComplete, teardownCheckout]);

    if (!isActive) {
        return null;
    }

    const formattedAmount = useMemo(() => {
        if (typeof amount !== 'number') {
            return null;
        }

        try {
            const formatter = new Intl.NumberFormat('en-GB', {
                style: 'currency',
                currency: resolvedCurrency
            });
            return formatter.format(amount);
        } catch (error) {
            console.warn('Failed to format currency for Stripe payment summary', error);
            return `${resolvedCurrency} ${amount.toFixed(2)}`;
        }
    }, [amount, resolvedCurrency]);

    return (
        <div className="payment-view__widget" aria-live="polite" aria-busy={isLoading}>
            <div id={containerId} className="payment-view__widget-container" />
            {formattedAmount && (
                <p className="payment-view__helper" role="status">
                    Order total {formattedAmount}
                    {orderId ? ` · Ref #${formatOrderIdForDisplay(orderId)}` : ''}
                </p>
            )}
            {isLoading && (
                <p className="payment-view__status payment-view__status--info">
                    Loading secure payment widget…
                </p>
            )}
            {status && (
                <div
                    className={`payment-view__status payment-view__status--${status.tone}`}
                    role={status.tone === 'error' || status.tone === 'warning' ? 'alert' : 'status'}
                >
                    {status.message}
                </div>
            )}
        </div>
    );
};

export default StripePayment;
